import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroupMUI from '@mui/material/RadioGroup';
import Typography from '@mui/material/Typography';
import { ReactElement, ReactNode } from 'react';
import { useFormContext } from 'react-hook-form';

import { IDatesValidation } from '~/services/api/clientAllocation';
import DateRangePicker from '~/ui/components/inputs/DateRangePicker';
import Checkbox from '~/ui/components/inputs/Checkbox';
import {
  AllocationMode,
  allocationModeOptions,
} from '~/ui/pages/ActTeam/reusable/ClientAllocationForm/constants/allocationMode';
import { useAddClientAllocationContext } from '~/ui/pages/ActTeam/reusable/ClientAllocationForm/AddClientAllocation/context/AddClientAllocationContext';
import { IAddAllocationFormValues } from '~/ui/pages/ActTeam/reusable/ClientAllocationForm/AddClientAllocation/types';

import styles from './AddAllocationFormLayout.module.scss';

interface IProps {
  title: string;
  isRestored: boolean;
  children: ReactNode;
  datesValidation: IDatesValidation;
}

const AddAllocationFormLayout = ({
  title,
  isRestored,
  datesValidation,
  children,
}: IProps): ReactElement => {
  const { allocationMode, setAllocationMode } = useAddClientAllocationContext();

  const {
    control,
    formState: { errors },
  } = useFormContext<IAddAllocationFormValues>();

  return (
    <Paper>
      <Box
        sx={{
          p: 2,
        }}
      >
        <Grid container spacing={2}>
          <Grid
            className={styles.titleRow}
            size={{
              sm: 12,
            }}
          >
            <Typography variant="subtitle1" className={styles.assignedClientsTitle}>
              {title}
            </Typography>
          </Grid>

          <Grid
            size={{
              sm: 4,
            }}
          >
            <DateRangePicker
              label="Select Date Range"
              control={control}
              name="dateRange"
              errors={errors}
            />
          </Grid>

          <Grid
            size={{
              sm: 3,
            }}
          >
            <Checkbox
              name="includeWeekends"
              size="small"
              control={control}
              label="Include weekends"
              errors={errors}
            />
          </Grid>

          <Grid
            size={{
              sm: 5,
            }}
          >
            <RadioGroupMUI
              row
              value={allocationMode}
              onChange={(_, value) => setAllocationMode(value as AllocationMode)}
            >
              {allocationModeOptions.map(option => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  label={option.label}
                  control={<Radio color="primary" size="small" />}
                />
              ))}
            </RadioGroupMUI>
          </Grid>
        </Grid>

        {!datesValidation?.isValid && !!datesValidation?.users?.length && (
          <Grid
            container
            direction="row"
            size={{
              sm: 12,
            }}
            sx={{
              alignItems: 'center',
            }}
          >
            <h5 className={styles.validationErrors}>
              {`Note: ${datesValidation.users.length} team member${
                datesValidation.users.length > 1 ? 's' : ''
              } already ${
                datesValidation.users.length > 1 ? 'have' : 'has'
              } allocations within this period!`}
            </h5>
          </Grid>
        )}

        {isRestored && (
          <div className={styles.formWrapper} data-menu-scroll-container>
            {children}
          </div>
        )}
      </Box>
    </Paper>
  );
};

export default AddAllocationFormLayout;
